import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { type UserManagedDocument } from "~/api/v2/useUserDocumentRoles";
import { Checkbox } from "~/atoms/checkbox";
import {
  EmptyCellValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/atoms/table";
import { useBulkHoverHighlight } from "~/hooks/useBulkHoverHighlight";
import { type BulkSelection } from "~/hooks/useBulkSelection";
import { type Option } from "~/molecules/ControlledCombobox";
import { ComboboxCell } from "~/molecules/onboard-documents/ComboboxCell";
import { MANAGER_COLUMNS, type ManagerColumn } from "../useOffboardUserPage";

interface Props {
  documents: UserManagedDocument[];
  offboardedUserId: number;
  userOptions: Option[] | undefined;
  selection: BulkSelection<number>;
  disabled: boolean;
  getManagerId: (
    document: UserManagedDocument,
    column: ManagerColumn,
  ) => number | null;
  getCellError: (
    document: UserManagedDocument,
    column: ManagerColumn,
  ) => string | undefined;
  onManagerChange: (
    column: ManagerColumn,
    documentIds: Set<number>,
    value: number,
  ) => void;
}

export const OffboardDocumentsTable = ({
  documents,
  offboardedUserId,
  userOptions,
  selection,
  disabled,
  getManagerId,
  getCellError,
  onManagerChange,
}: Props) => {
  const { t } = useTranslation();
  const { selectedIds, allSelected, toggleRow, toggleAll, bulkTargets } =
    selection;
  const { onHover, onHoverLeave, isHighlighted } = useBulkHoverHighlight<
    number,
    ManagerColumn
  >(selectedIds);

  return (
    <Table
      className="bg-card table-fixed"
      wrapperClassName="max-h-[60vh] rounded-md border"
    >
      <TableHeader className="bg-muted sticky top-0 z-10">
        <TableRow>
          <TableHead className="w-12">
            <Checkbox
              checked={allSelected}
              onCheckedChange={toggleAll}
              disabled={disabled}
            />
          </TableHead>
          <TableHead>{t(($) => $.commons.document.title)}</TableHead>
          <TableHead>{t(($) => $.commons.document.owner)}</TableHead>
          <TableHead>{t(($) => $.commons.document.publisher)}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {documents.map((document) => (
          <TableRow key={document.documentId}>
            <TableCell>
              <Checkbox
                checked={selectedIds.has(document.documentId)}
                onCheckedChange={() => toggleRow(document.documentId)}
                disabled={disabled}
              />
            </TableCell>
            <TableCell>
              <Link
                to={`/documents/${document.documentId}`}
                className="block truncate hover:underline"
              >
                {document.title}
              </Link>
            </TableCell>
            {MANAGER_COLUMNS.map((column) => {
              if (document[column]?.id !== offboardedUserId) {
                return (
                  <TableCell key={column}>
                    <EmptyCellValue />
                  </TableCell>
                );
              }
              const error = getCellError(document, column);
              return (
                <TableCell key={column}>
                  <ComboboxCell
                    value={getManagerId(document, column)?.toString() ?? null}
                    onChange={(value) =>
                      onManagerChange(
                        column,
                        bulkTargets(document.documentId),
                        Number(value),
                      )
                    }
                    options={userOptions}
                    placeholder={
                      column === "owner"
                        ? t(($) => $.commons.customSelect.enterOwner)
                        : t(($) => $.commons.customSelect.enterPublisher)
                    }
                    disabled={disabled}
                    hasError={error !== undefined}
                    errorMessage={error}
                    highlighted={isHighlighted(document.documentId, column)}
                    onHover={() => onHover(document.documentId, column)}
                    onHoverLeave={onHoverLeave}
                  />
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
